import { clsx } from "clsx";
import { PureComponent, type ReactElement } from "react";
import { connect } from "react-redux";

import { LoadingSpinner } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { type IRsqlFilterTree } from "../../app-utils/rsql.js";
import { type ISelectionModel, isSelected } from "../../app-utils/selection-model.js";
import { type RawAttributeDefault, type SampleType, type SearchResult, type User } from "../../graphql.js";
import {
  fetchFullSampleTypeWithAttributeDefaults,
  loadSampleTypes,
  onSampleTypeSelectionChange,
} from "../../redux/modules/sample-admin.js";
import { createAdvancedSearchResultAndRedirect } from "../../redux/modules/search-results.js";
import { type IAppState } from "../../redux/reducer.js";
import { AdvancedSearch } from "../core/navbar/AdvancedSearch.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import SampleTypeItem from "./SampleTypeItem.js";
import SampleTypesAdminToolbar from "./SampleTypesAdminToolbar.js";
import { ENTITY_TYPE } from "@/settings/settings.ts";

interface ISampleTypesAdminPageOwnProps {}

interface ISampleTypesAdminPageAppStateProps {
  // Redux injected props
  sampleTypes: SampleType[];
  selectionModel: ISelectionModel;
  user: User;
}

interface ISampleTypesAdminPageDispatchProps {
  loadSampleTypes: () => Promise<SampleType[]>;
  onSelectionChange: (sampleType: SampleType, selected: boolean) => void;
  createSearchResult: (filterConstraint: IRsqlFilterTree) => Promise<SearchResult>;
  fetchFullSampleTypeWithAttributeDefaults: (
    sampleTypeId: number,
  ) => Promise<{ sampleType: SampleType; attributeDefaults: RawAttributeDefault[] }>;
}

export interface ISampleTypesAdminPageProps
  extends ISampleTypesAdminPageAppStateProps,
    ISampleTypesAdminPageDispatchProps,
    ISampleTypesAdminPageOwnProps {}

function mapStateToProps(
  state: IAppState,
  _ownProps: ISampleTypesAdminPageOwnProps,
): ISampleTypesAdminPageAppStateProps {
  return {
    sampleTypes: state.sampleAdmin.sampleTypes,
    selectionModel: state.sampleAdmin.sampleTypesSelectionModel,
    user: state.auth.session,
  };
}

function mapDispatchToProps(dispatch): ISampleTypesAdminPageDispatchProps {
  return {
    loadSampleTypes: () => dispatch(loadSampleTypes()),
    onSelectionChange: (sampleType: SampleType, selected: boolean) =>
      dispatch(onSampleTypeSelectionChange(sampleType, selected)),
    createSearchResult: (filterConstraint: IRsqlFilterTree) =>
      dispatch(
        createAdvancedSearchResultAndRedirect(ENTITY_TYPE.SAMPLE, undefined, undefined, filterConstraint, undefined),
      ),
    fetchFullSampleTypeWithAttributeDefaults: (sampleTypeId: number) =>
      dispatch(fetchFullSampleTypeWithAttributeDefaults(sampleTypeId)),
  };
}

interface ISampleTypesAdminPageState {
  isLoadingSampleTypes: boolean;
}

class SampleTypesAdminPage extends PureComponent<ISampleTypesAdminPageProps, ISampleTypesAdminPageState> {
  constructor(props: ISampleTypesAdminPageProps) {
    super(props);

    this.state = {
      isLoadingSampleTypes: false,
    };
  }

  loadSamplTypes = () => {
    this.setState({ isLoadingSampleTypes: true });
    this.props.loadSampleTypes().then(() => this.setState({ isLoadingSampleTypes: false }));
  };

  componentDidMount() {
    this.loadSamplTypes();
  }

  listAllSamples = (sampleType: SampleType) => {
    const filterConstraint: IRsqlFilterTree = AdvancedSearch.getFilterConstraints([], [], [sampleType], []);
    return this.props.createSearchResult(filterConstraint);
  };

  render(): ReactElement {
    return (
      <div className={clsx(styleLayout.page, styleLayout.page_scrollable)} style={{ backgroundColor: "white" }}>
        <PageHeader
          title={"Sample types"}
          badge={String(this.props.sampleTypes.length)}
          actionWidgets={[<SampleTypesAdminToolbar key={"sample-types-admin-page-action-1"} />]}
        />
        {this.state.isLoadingSampleTypes ? (
          <LoadingSpinner inCenter />
        ) : (
          <div style={{ height: "calc(100% - 50px)", overflow: "auto" }}>
            <ul className="saved-items">
              {this.props.sampleTypes.map((sampleType: SampleType) => (
                <SampleTypeItem
                  key={`sample-type-key-${sampleType.id}`}
                  sampleType={sampleType}
                  user={this.props.user}
                  isSelected={isSelected(this.props.selectionModel, sampleType.id)}
                  onItemSelectionChange={this.props.onSelectionChange}
                  listAllSamples={this.listAllSamples}
                  fetchFullSampleTypeWithAttributeDefaults={this.props.fetchFullSampleTypeWithAttributeDefaults}
                />
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }
}

export default connect<
  ISampleTypesAdminPageAppStateProps,
  ISampleTypesAdminPageDispatchProps,
  ISampleTypesAdminPageOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(SampleTypesAdminPage);
